import { useCallback, useEffect, useRef } from 'react';
import { useFetchClient } from './fetch';
import { ENV } from 'config/env';

const useChatSocket = (conversationId, token, onMessage, onRead) => {
  const wsRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const url = ENV.MSG_WSS_URL;
  const { fetchClient } = useFetchClient();
  const reconnectTimeoutRef = useRef(null);
  const eventHandlersRef = useRef({});
  const onReadRef = useRef(onRead);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const readMessage = useCallback(
    async (convID) => {
      try {
        await fetchClient(`chats/read/${convID}`, 'GET', {}, { silent: true });
        if (onReadRef.current) {
          onReadRef.current(convID);
        }
      } catch (error) {
        console.warn('Failed to mark messages as read:', error?.message);
      }
    },
    [fetchClient]
  );

  const setOnlineState = useCallback(
    async (convID, status) => {
      try {
        await fetchClient(
          `conversation/online`,
          'POST',
          {
            conversationId: convID,
            isOnline: status,
          },
          { silent: true }
        );
        return true;
      } catch (error) {
        console.warn('Failed to update chat online status:', error?.message);
        return false;
      }
    },
    [fetchClient]
  );

  const emit = useCallback((event, data) => {
    if (eventHandlersRef.current[event]) {
      eventHandlersRef.current[event].forEach((handler) => handler(data));
    }
  }, []);

  useEffect(() => {
    if (!conversationId || !token) return;
    let isDisposed = false;
    let reconnectAttempts = 0;
    let hasConnected = false;
    let onlineState = null;
    let stableConnectionTimeout = null;
    let offlineTimeout = null;

    const updateOnlineState = async (status) => {
      if (onlineState === status) return;
      onlineState = status;

      const updated = await setOnlineState(conversationId, status);
      if (!updated && onlineState === status) {
        onlineState = null;
      }
    };

    const scheduleReconnect = () => {
      if (isDisposed || reconnectTimeoutRef.current) return;

      const baseDelay = Math.min(3000 * 2 ** reconnectAttempts, 30000);
      const delay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));
      reconnectAttempts += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (isDisposed) return;

      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const wsUrl = `${url}chats/ws?conversationId=${conversationId}&token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isDisposed || wsRef.current !== ws) {
          ws.close();
          return;
        }

        hasConnected = true;
        if (offlineTimeout) {
          clearTimeout(offlineTimeout);
          offlineTimeout = null;
        }
        if (stableConnectionTimeout) {
          clearTimeout(stableConnectionTimeout);
        }
        stableConnectionTimeout = setTimeout(() => {
          reconnectAttempts = 0;
          stableConnectionTimeout = null;
        }, 30000);

        readMessage(conversationId);
        updateOnlineState(true);
      };

      ws.onmessage = (event) => {
        if (isDisposed || wsRef.current !== ws) return;

        try {
          const data = JSON.parse(event.data);
          const msg = data.data;
          const acceptedType = ['chat_created', 'conversation_updated', 'typing'];

          if (data.status !== true || !acceptedType.includes(data.type)) return;

          if (data.type === 'chat_created') {
            if (Array.isArray(msg)) {
              msg
                .slice()
                .reverse()
                .forEach((item) => {
                  emit('chat_created', { data: item });
                  if (onMessageRef.current) {
                    onMessageRef.current(item);
                  }
                });
            } else {
              emit('chat_created', { data: msg });
              if (onMessageRef.current) {
                onMessageRef.current(msg);
              }
            }
          } else if (data.type === 'typing') {
            emit('typing', msg);
            if (onMessageRef.current) {
              onMessageRef.current({ type: 'typing', data: msg });
            }
          }
        } catch (e) {
          console.error('Error parsing chat socket message:', e);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (isDisposed) return;

        if (stableConnectionTimeout) {
          clearTimeout(stableConnectionTimeout);
          stableConnectionTimeout = null;
        }

        if (hasConnected && !offlineTimeout) {
          offlineTimeout = setTimeout(() => {
            offlineTimeout = null;
            const currentSocket = wsRef.current;
            const isConnecting =
              currentSocket &&
              (currentSocket.readyState === WebSocket.OPEN ||
                currentSocket.readyState === WebSocket.CONNECTING);

            if (!isDisposed && !isConnecting) {
              updateOnlineState(false);
            }
          }, 5000);
        }

        scheduleReconnect();
      };

      ws.onerror = (e) => {
        if (isDisposed || wsRef.current !== ws) return;
        console.error('Chat socket error:', e);
        ws.close();
      };
    };

    connect();

    return () => {
      isDisposed = true;
      if (wsRef.current) {
        const currentSocket = wsRef.current;
        wsRef.current = null;
        currentSocket.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (offlineTimeout) {
        clearTimeout(offlineTimeout);
      }
      if (stableConnectionTimeout) {
        clearTimeout(stableConnectionTimeout);
      }
    };
  }, [conversationId, token, url, readMessage, setOnlineState]);
};

export default useChatSocket;

export const useConversationSocket = (token, onUpdate, onStatusChange) => {
  const wsRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  const url = ENV.MSG_WSS_URL;
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!token) return;
    let isDisposed = false;
    let reconnectAttempts = 0;
    let stableConnectionTimeout = null;

    const scheduleReconnect = () => {
      if (isDisposed || reconnectTimeoutRef.current) return;

      const baseDelay = Math.min(3000 * 2 ** reconnectAttempts, 30000);
      const delay = Math.round(baseDelay * (0.8 + Math.random() * 0.4));
      reconnectAttempts += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (isDisposed) return;

      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const wsUrl = `${url}conversation/ws?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isDisposed || wsRef.current !== ws) {
          ws.close();
          return;
        }

        if (stableConnectionTimeout) {
          clearTimeout(stableConnectionTimeout);
        }
        stableConnectionTimeout = setTimeout(() => {
          reconnectAttempts = 0;
          stableConnectionTimeout = null;
        }, 30000);
        onStatusChange?.('connected');
      };

      ws.onmessage = (event) => {
        if (isDisposed || wsRef.current !== ws) return;

        try {
          const message = JSON.parse(event.data);
          onUpdateRef.current?.(message.data);
        } catch (e) {
          console.error('Error parsing conversation socket message:', e);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (isDisposed) return;

        if (stableConnectionTimeout) {
          clearTimeout(stableConnectionTimeout);
          stableConnectionTimeout = null;
        }

        onStatusChange?.('reconnecting');
        scheduleReconnect();
      };

      ws.onerror = (e) => {
        if (isDisposed || wsRef.current !== ws) return;
        console.error('Conversation socket error:', e);
        onStatusChange?.('disconnected');
        ws.close();
      };
    };

    connect();

    return () => {
      isDisposed = true;
      if (wsRef.current) {
        const currentSocket = wsRef.current;
        wsRef.current = null;
        currentSocket.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (stableConnectionTimeout) {
        clearTimeout(stableConnectionTimeout);
      }
    };
  }, [token, url]);
};
